/**
 * Reconnecting WebSocket client for /api/ws/conversation/:id (Phase 5).
 *
 * Same client used by buyers AND admins — the only difference is the token.
 *
 * Usage:
 *   const conn = createConversationWS({
 *     conversationId, token,
 *     onOpen, onClose, onMessage, onTyping, onRead, onPresence, onError,
 *   });
 *   conn.send("hello", []);
 *   conn.typing(true);
 *   conn.read("<message_id>");
 *   conn.close();
 *
 * Reconnect: exponential backoff capped at 30s. Close codes 4401/4403/4404
 * are *terminal* (don't reconnect).
 */
import { BACKEND_BASE } from "./api";

const WS_BASE = (BACKEND_BASE || "").replace(/^http/i, "ws");

const TERMINAL_CODES = new Set([4401, 4403, 4404]);

export function createConversationWS({
  conversationId,
  token,
  onOpen,
  onClose,
  onMessage,
  onTyping,
  onRead,
  onPresence,
  onError,
}) {
  let ws = null;
  let closedByUser = false;
  let backoffMs = 1000;
  let pingTimer = null;
  let reconnectTimer = null;

  const url = `${WS_BASE}/api/ws/conversation/${encodeURIComponent(
    conversationId,
  )}?token=${encodeURIComponent(token)}`;

  const clearTimers = () => {
    if (pingTimer) clearInterval(pingTimer);
    if (reconnectTimer) clearTimeout(reconnectTimer);
    pingTimer = null;
    reconnectTimer = null;
  };

  const connect = () => {
    closedByUser = false;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      onError?.(e);
      return;
    }
    ws.onopen = () => {
      backoffMs = 1000;
      onOpen?.();
      pingTimer = setInterval(() => {
        try {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        } catch {
          /* noop */
        }
      }, 25000);
    };
    ws.onmessage = (evt) => {
      let data;
      try {
        data = JSON.parse(evt.data);
      } catch {
        return;
      }
      switch (data.type) {
        case "message":
          onMessage?.(data.message);
          break;
        case "typing":
          onTyping?.(data);
          break;
        case "read":
          onRead?.(data);
          break;
        case "presence":
          onPresence?.(data);
          break;
        case "pong":
        default:
          break;
      }
    };
    ws.onerror = (e) => onError?.(e);
    ws.onclose = (e) => {
      clearTimers();
      onClose?.(e);
      if (closedByUser) return;
      if (TERMINAL_CODES.has(e.code)) return;
      const wait = Math.min(backoffMs, 30000);
      backoffMs = Math.min(backoffMs * 2, 30000);
      reconnectTimer = setTimeout(connect, wait);
    };
  };

  connect();

  return {
    send: (body, attachments) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return false;
      ws.send(
        JSON.stringify({
          type: "send",
          body: body || "",
          attachments: attachments || [],
        }),
      );
      return true;
    },
    typing: (isTyping) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "typing", is_typing: !!isTyping }));
    },
    read: (upToMessageId) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(
        JSON.stringify({ type: "read", up_to_message_id: upToMessageId }),
      );
    },
    close: () => {
      closedByUser = true;
      clearTimers();
      try {
        ws?.close();
      } catch {
        /* noop */
      }
    },
    state: () => ws?.readyState ?? -1,
    isOpen: () => ws?.readyState === WebSocket.OPEN,
  };
}
